import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home", id: "nav-link-home" },
  { to: "/about", label: "About Us", id: "nav-link-about" },
  { to: "/recruiting", label: "Recruiting", id: "nav-link-recruiting" },
  { to: "/schedule", label: "Schedule", id: "nav-link-schedule" },
  { to: "/roster", label: "Roster", id: "nav-link-roster" },
  { to: "/camps", label: "Camps", id: "nav-link-camps" },
  { to: "/contact", label: "Contact", id: "nav-link-contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
          scrolled || open ? "glass border-b border-white/10" : "border-b border-transparent bg-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img src="/logo.png" alt="Alabama Bulldogs Sports Academy logo" className="h-11 w-11 object-contain" />
            <span className="flex flex-col leading-none">
              <span className="font-display text-lg font-extrabold uppercase tracking-wide text-white">
                Alabama Bulldogs
              </span>
              <span className="mt-1 font-mono text-[10px] font-semibold uppercase tracking-[0.32em] text-ice">
                Sports Academy
              </span>
            </span>
          </Link>

          <div className="hidden items-center gap-6 lg:flex xl:gap-7">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.id}
                className={({ isActive }) =>
                  `link-underline font-display text-[15px] font-bold uppercase tracking-wider transition-colors ${
                    isActive ? "is-active text-white" : "text-silver-dim hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/#apply"
              data-testid="nav-apply-btn"
              className="hidden items-center gap-1.5 rounded-full bg-crimson px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-crimson-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice active:scale-[0.98] sm:inline-flex"
            >
              Apply Now <ArrowUpRight className="h-4 w-4" />
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-menu-toggle"
              aria-label="Toggle menu"
              className="grid h-11 w-11 place-items-center rounded-lg border border-white/10 text-white transition-colors hover:border-ice/50 lg:hidden"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-40 bg-navy-900/95 pt-24 backdrop-blur-xl lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col px-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.05, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`nav-mobile-${l.id}`}
                    className={({ isActive }) =>
                      `block border-b border-white/5 py-4 font-display text-3xl font-extrabold uppercase tracking-wide transition-colors ${
                        isActive ? "text-ice" : "text-white"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.35 }}
                className="pt-6"
              >
                <Link
                  to="/#apply"
                  data-testid="nav-mobile-apply-btn"
                  className="flex items-center justify-center gap-2 rounded-full bg-crimson px-6 py-4 font-display text-lg font-bold uppercase tracking-wider text-white"
                >
                  Apply Now <ArrowUpRight className="h-5 w-5" />
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
